import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { criteriaOperatorsMap } from '../../containers/base-filter-bar-container/criteria-operators';
import { useIs3PLPlanEnabledForSeller } from '../../hooks/use-is-3pl-plan-enabled-for-seller';
import { useInventoryTrackingEnabled } from '../../hooks/use-seller-setting';
import Global from '../../store/global-selectors';
import {
  autoSwitchFilterViewToCustom as autoSwitchFilterViewToCustomFn,
  autoSwitchFilterViewToDefault as autoSwitchFilterViewToDefaultFn,
  changeCustomFilterViewProp as changeCustomFilterViewPropFn,
} from '../../store/orders/filtering/orders.filtering.actions';
import { orderGridChangePage } from '../../store/orders/grid/orders.grid.actions';
import { requestGetOrders as requestGetOrdersFn } from '../../store/orders/orders.actions';

import { serviceCollectionsNameCorrection } from '../../containers/base-filter-bar-container/reference-data-criteria-arguments';
import { usePickPackFeatureEnabled } from '../../feature-flags/hooks/use-pick-pack-feature-enabled';
import { useEnablePickingExceptions } from '../../feature-flags/hooks/use-enable-picking-exceptions';
import { SERVICE_COLLECTION, WAREHOUSE_STATUS, PICKING_EXCEPTION } from '../../filters/constants';
import { ACCESS_LEVELS, useRequiresRateShopperAccessLevel } from '../../hooks/use-has-rate-shopper-access-level';
import { useSortedTranslatedCountries } from '../../hooks/use-sorted-translated-countries';
import { useSelectedOrdersViewId } from '../../pages/orders-page/orders-left-nav/orders-left-nav-context';
import { ON_HOLD_ID } from '../../pages/orders-page/orders-page-view-constants';
import { getCriteriaArguments } from './criteria-arguments';
import { otherFilterByOptions, popularFilterByOptions } from './filter-options';
import { BRAND_FILTER_ID } from './filter-options/options/seller-brand';

const implicitFilterByIds = ['assignedTo'];
const allOptions = [...popularFilterByOptions, ...otherFilterByOptions];

const otherOptionsSelector = createSelector(
  (isInventoryEnabled) => isInventoryEnabled,
  (_, isUserLocked) => isUserLocked,
  (_, __, isServiceCollectionsEnabled) => isServiceCollectionsEnabled,
  (_, __, ___, isPickingEnabled) => isPickingEnabled,
  (_, __, ___, ____, isPickingExceptionsEnabled) => isPickingExceptionsEnabled,
  (_, __, ___, ____, _____, isOnHoldView) => isOnHoldView,
  (isInventoryEnabled, isUserLocked, isServiceCollectionsEnabled, isPickingEnabled, isPickingExceptionsEnabled, isOnHoldView) => {
    let filteredOptions = otherFilterByOptions;

    filteredOptions = isInventoryEnabled ? filteredOptions : filteredOptions.filter((filter) => !filter.isInventoryFilter);

    filteredOptions = isUserLocked ? filteredOptions : filteredOptions.filter((filter) => filter.id !== 'assignedTo');

    filteredOptions = isServiceCollectionsEnabled ? filteredOptions : filteredOptions.filter((filter) => filter.id !== SERVICE_COLLECTION);

    filteredOptions = isPickingEnabled ? filteredOptions : filteredOptions.filter((filter) => filter.id !== WAREHOUSE_STATUS);

    filteredOptions =
      isPickingExceptionsEnabled && isOnHoldView ? filteredOptions : filteredOptions.filter((filter) => filter.id !== PICKING_EXCEPTION);

    return filteredOptions;
  },
);

const flaggedPopularFilterSelector = createSelector(
  (isV33PLPlanBetaEnabled) => isV33PLPlanBetaEnabled,
  (isV33PLPlanBetaEnabled) => {
    if (isV33PLPlanBetaEnabled) {
      return popularFilterByOptions;
    }
    return popularFilterByOptions.filter(({ id }) => id !== BRAND_FILTER_ID);
  },
);

export const useOrdersFilterBarState = () => {
  const criteriaArgumentsMap = useSelector(getCriteriaArguments);
  const intl = useIntl();
  const countries = useSortedTranslatedCountries({ labelProp: 'displayName' });
  const correctedCriteriaArgumentsMap = useMemo(() => {
    const correctedServiceCollections = serviceCollectionsNameCorrection({
      intl,
      serviceCollections: criteriaArgumentsMap?.serviceCollection,
    });
    const correctedCounties = countries.map((country) => ({
      ...country,
      id: 'option-country',
      value: country.code,
    }));
    return {
      ...criteriaArgumentsMap,
      serviceCollection: correctedServiceCollections,
      country: correctedCounties,
    };
  }, [intl, criteriaArgumentsMap, countries]);

  const currentFilterView = useSelector(Global.selectOrdersFilteringActiveFilterView);
  const { appliedFilters } = currentFilterView;
  const currentViewId = useSelectedOrdersViewId();
  const isOnHoldView = currentViewId === ON_HOLD_ID;
  const is3PLPlanEnabled = useIs3PLPlanEnabledForSeller();
  const isInventoryEnabled = useInventoryTrackingEnabled();
  const showRateShopper = useRequiresRateShopperAccessLevel(ACCESS_LEVELS.FREEMIUM);
  const isUserLocked = useSelector(Global.selectUseruserHasOrderViewRestriction);
  const isPickingEnabled = usePickPackFeatureEnabled();
  const isPickingExceptionsEnabled = useEnablePickingExceptions();
  const popularFilterBys = flaggedPopularFilterSelector(is3PLPlanEnabled);
  const otherFilterBys = otherOptionsSelector(
    isInventoryEnabled,
    isUserLocked,
    showRateShopper,
    isPickingEnabled,
    isPickingExceptionsEnabled,
    isOnHoldView,
  );

  const filteredOptions = useMemo(() => {
    return allOptions.filter((option) => {
      if (option.id === SERVICE_COLLECTION) {
        return showRateShopper;
      }
      if (option.id === WAREHOUSE_STATUS) {
        return isPickingEnabled;
      }
      if (option.id === PICKING_EXCEPTION) {
        return isPickingExceptionsEnabled && isOnHoldView;
      }

      return true;
    });
  }, [isPickingEnabled, showRateShopper, isPickingExceptionsEnabled, isOnHoldView]);

  return {
    allFilterBys: filteredOptions,
    appliedFilters,
    criteriaArgumentsMap: correctedCriteriaArgumentsMap,
    criteriaOperatorsMap,
    currentFilterView,
    implicitFilterByIds,
    otherFilterBys,
    popularFilterBys,
  };
};

export const useOrdersFilterBarDispatch = () => {
  const dispatch = useDispatch();
  return {
    autoSwitchFilterViewToCustom: useCallback((...args) => dispatch(autoSwitchFilterViewToCustomFn(...args)), [dispatch]),
    autoSwitchFilterViewToDefault: useCallback((...args) => dispatch(autoSwitchFilterViewToDefaultFn(...args)), [dispatch]),
    changeCustomFilterViewProp: useCallback((...args) => dispatch(changeCustomFilterViewPropFn(...args)), [dispatch]),
    requestGetOrders: useCallback((...args) => dispatch(requestGetOrdersFn(...args)), [dispatch]),
    resetToPageOne: useCallback(() => dispatch(orderGridChangePage(1)), [dispatch]),
  };
};
